import React, { useState, useEffect } from 'react';
import getConfig from 'next/config';
import { useDispatch, useSelector } from 'react-redux';
import { GetServerSideProps } from 'next';
import { Row, Divider, Col, List, Button, Carousel, Tooltip, Modal } from 'antd';
import styled from 'styled-components';
import Link from 'next/link';
import NImage from 'next/image';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import BasicLayout from 'layout/Basic';
import Image from 'components/Image';
import { convertProps } from 'lib/parseAPIResponse';
import Content from 'layout/Basic/Content';
import Sider from 'layout/Basic/Sider';
import Tags from 'components/Tags';
import HtmlDescription from 'components/HtmlDescription';
import { SkyscraperMobile, Wideboard } from 'components/Ads';
import Newsletter from 'containers/Newsletter';
import Subscription from 'containers/Subscription';
import Section, { SectionTitle } from 'components/Section';
import { State } from 'reducer/reducer';
import { getToken, isTokenValid } from 'lib/authentication';
import * as gtag from 'lib/gtag';
import { logout, openLoginModal } from 'reducer/actions';
import WhitePaperBG from 'public/static/images/whitepaper-bg.jpg';
import WhitePaperDefault from 'public/static/images/whitepaper-default.svg';
import { merriweather } from 'styles/GlobalStyle';

const DynamicForm = dynamic(() => import('components/DynamicForm'), {
    ssr: false
});

interface Props {
    whitePaper: WhitePaper;
    latestIssue: Issue;
    ads: PageAds;
}

interface GroupedByBranchs {
    [key: string]: {
        title: string;
        seoSlug: string;
        items: [
            {
                title: string;
                seoSlug: string;
            }
        ];
    };
}

const groupByParentCategory = (branches: Category[]): GroupedByBranchs => {
    return branches.reduce((r, a) => {
        if (a.lvl === 1) {
            r[a.seoSlug] = {
                title: a.title,
                seoSlug: a.seoSlug,
                items: []
            };
        } else {
            r[a.parent.seoSlug] = {
                title: a.parent.title,
                seoSlug: a.parent.seoSlug,
                items: [
                    ...(r[a.parent.seoSlug] || { items: [] }).items,
                    { title: a.title, seoSlug: a.seoSlug }
                ]
            };
        }
        return r;
    }, {});
};

const ListStyled = styled(List)`
    .ant-list-item {
        padding: 0px 16px;
        > h6 {
            margin-bottom: 2px;
        }
    }
    .ant-list-header {
        padding-top: 8px;
        padding-bottom: 6px;
    }
`;

const BranchTitle = styled.h5`
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    margin-bottom: 0;
    a {
        color: ${({ theme }) => theme.colors.greyishBrown};
    }
`;

const BranchName = styled.h6`
    font-size: 16px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    a {
        color: ${({ theme }) => theme.colors.greyishBrown};
    }
`;

const LogoContainer = styled.div`
    position: absolute;
    width: 25%;
    height: auto;
    top: 50%;
    left: 3%;
    background: white;
    border: 1px solid ${({ theme }) => theme.colors.lightBlue};
`;

const Title = styled.h1`
    font-size: 32px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.25;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const SubTitle = styled.h2`
    font-size: 20px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.25;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.primary};
`;

const DividerStyled = styled(Divider)`
    margin: 30px 0 50px 0;
`;

const LogoWrapper = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.lightBlue};

    > div {
        margin: 0 auto;
        display: block;
    }
`;

const HtmlDescriptionWrapper = styled.div`
    font-family: ${merriweather.style.fontFamily};
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const SectionTitleStyled = styled.h2`
    color: ${({ theme }) => theme.colors.primary};
    font-family: ${merriweather.style.fontFamily};
    font-size: 18px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 18px;
    -webkit-letter-spacing: 0.36px;
    -moz-letter-spacing: 0.36px;
    -ms-letter-spacing: 0.36px;
    letter-spacing: 0.36px;
    text-align: left;
    text-transform: uppercase;
    margin-bottom: 15px;
`;

interface DownloadButtonProps {
    whitePaper: WhitePaper;
    token: string;
    handleDownloadClick: (event: React.MouseEvent<HTMLElement>) => void;
    isLoggedIn: boolean;
}

const DownloadButton = ({
    whitePaper,
    token,
    handleDownloadClick,
    isLoggedIn
}: DownloadButtonProps): React.JSX.Element => {
    if (whitePaper.leadForm) {
        return <Button onClick={handleDownloadClick}>Pobierz zawartość</Button>;
    }
    if (whitePaper.downloadLink && !whitePaper.isSecure) {
        return <Button href={`${whitePaper.downloadLink}`}>Pobierz zawartość</Button>;
    }

    if (whitePaper.downloadLink && whitePaper.isSecure) {
        return (
            <Button
                href={`${whitePaper.downloadLink}?bearer=${token}`}
                onClick={handleDownloadClick}
            >
                {isLoggedIn ? 'Pobierz zawartość' : 'Musisz być zalogowany, aby pobrać zawartość'}
            </Button>
        );
    }
};

export const WhitePapersDetailsPage = ({
    whitePaper,
    latestIssue,
    ads
}: Props): React.JSX.Element => {
    const dispatch = useDispatch();
    const isLoggedIn = useSelector((state: State) => state.isLoggedIn);
    const [token, setToken] = useState<string>();
    const [showDynamicForm, setShowDynamicForm] = useState<boolean>(false);
    const router = useRouter();

    useEffect(() => {
        setToken(getToken());
    }, [isLoggedIn]);

    const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
        if (whitePaper.leadRequired) {
            setShowDynamicForm(true);
        } else {
            if (!isTokenValid()) {
                event.preventDefault();
                dispatch(logout());
                dispatch(openLoginModal());
                return;
            }
            gtag.event({
                action: 'Downloaded',
                category: 'White Paper',
                label: whitePaper.title,
                value: router.asPath
            });
        }
    };

    const onSubmitSuccess = () => {
        setShowDynamicForm(false);
    };

    const onCancelDynamicForm = () => {
        setShowDynamicForm(false);
    };

    const groupedBranches = Object.values(groupByParentCategory(whitePaper.categories));

    const companies = whitePaper.companies.filter((company: Company): boolean => company.premium);
    const { url } = getConfig().publicRuntimeConfig;
    const { skyscraper, wideboardBottom } = ads;
    return (
        <BasicLayout ads={ads}>
            <NextSeo
                title={whitePaper.seoTitle || whitePaper.title}
                canonical={`${url}/white-papers/${whitePaper.seoSlug}`}
                description={whitePaper.seoDescription || whitePaper.subtitle}
                openGraph={{
                    title: whitePaper.title,
                    description: whitePaper.seoDescription || whitePaper.subtitle,
                    url: `${url}/white-papers/${whitePaper.seoSlug}`,
                    images: [
                        {
                            url: whitePaper.openingImage,
                            width: 567,
                            height: 359,
                            alt: whitePaper.title
                        }
                    ]
                }}
            />
            <Row>
                <Col xs={24} md={12}>
                    <SkyscraperMobile id={skyscraper[0]} margin="0 auto" />
                </Col>
                <Col xs={24} md={12}>
                    <SkyscraperMobile id={skyscraper[1]} margin="35px auto 35px auto" />
                </Col>
            </Row>
            <Row style={{ position: 'relative' }}>
                <Col span={24}>
                    <NImage
                        width={1200}
                        height={303}
                        src={WhitePaperBG}
                        alt={whitePaper.title}
                        style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                    />
                    <LogoContainer>
                        {whitePaper.openingImage ? (
                            <Image
                                type="white-paper"
                                src={whitePaper.openingImage}
                                height={300}
                                width={300}
                                alt={whitePaper.title}
                                style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                            />
                        ) : (
                            <NImage
                                height={300}
                                width={300}
                                src={WhitePaperDefault}
                                alt={whitePaper.title}
                                style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                            />
                        )}
                    </LogoContainer>
                </Col>
            </Row>
            <Section>
                <Col span={24} md={{ offset: 8, span: 16 }}>
                    <Title>{whitePaper.title}</Title>
                    <SubTitle>{whitePaper.subtitle}</SubTitle>
                    <DividerStyled />
                </Col>
                <Col xs={{ span: 8, offset: 8 }} md={24}>
                    <DownloadButton
                        whitePaper={whitePaper}
                        handleDownloadClick={handleDownloadClick}
                        token={token}
                        isLoggedIn={isLoggedIn}
                    />
                </Col>
                {companies.length > 0 && (
                    <Col span={6}>
                        {companies.length === 1 ? (
                            <LogoWrapper>
                                <Link href={`/katalog-firm/${whitePaper.companies[0].seoSlug}`}>
                                    <Tooltip title={whitePaper.companies[0].name}>
                                        <Image
                                            alt={whitePaper.companies[0].name}
                                            type="logo"
                                            width={272}
                                            height={272}
                                            src={whitePaper.companies[0].logo}
                                        />
                                    </Tooltip>
                                </Link>
                            </LogoWrapper>
                        ) : (
                            <Carousel dotPosition="left" autoplaySpeed={7000} autoplay>
                                {whitePaper.companies.map((company) => (
                                    <LogoWrapper key={company.seoSlug}>
                                        <Link href={`/katalog-firm/${company.seoSlug}`}>
                                            <Tooltip title={company.name}>
                                                <Image
                                                    alt={company.name}
                                                    type="logo"
                                                    width={272}
                                                    height={272}
                                                    src={company.logo}
                                                />
                                            </Tooltip>
                                        </Link>
                                    </LogoWrapper>
                                ))}
                            </Carousel>
                        )}
                    </Col>
                )}
            </Section>
            <Divider />
            <Row gutter={24}>
                <Content>
                    <Row>
                        <Col span={8}>
                            <SectionTitle title="Opis" />
                        </Col>
                        <Col span={15}>
                            <HtmlDescriptionWrapper>
                                <HtmlDescription>{whitePaper.content}</HtmlDescription>
                            </HtmlDescriptionWrapper>
                        </Col>
                    </Row>
                    <Section justify="start">
                        <Col xs={{ span: 8, offset: 8 }} md={24}>
                            <DownloadButton
                                whitePaper={whitePaper}
                                handleDownloadClick={handleDownloadClick}
                                token={token}
                                isLoggedIn={isLoggedIn}
                            />
                        </Col>
                    </Section>
                </Content>
                <Sider>
                    {groupedBranches.length > 0 && (
                        <Row>
                            <Col span={24}>
                                <SectionTitleStyled>Branże</SectionTitleStyled>
                            </Col>
                            {groupedBranches.map((branch) => {
                                if (branch.items.length > 0) {
                                    return (
                                        <Col span={24} key={branch.seoSlug}>
                                            <ListStyled
                                                split={false}
                                                rowKey={({ seoSlug }: Branch): string => seoSlug}
                                                size="small"
                                                header={
                                                    <BranchTitle>
                                                        <Link
                                                            href={`/katalog-firm?branch=${branch.seoSlug}`}
                                                        >
                                                            {branch.title}
                                                        </Link>
                                                    </BranchTitle>
                                                }
                                                dataSource={branch.items}
                                                renderItem={(item: Branch) => (
                                                    <List.Item>
                                                        <BranchName>
                                                            <Link
                                                                href={`/katalog-firm?branch=${item.seoSlug}`}
                                                            >
                                                                {item.title}
                                                            </Link>
                                                        </BranchName>
                                                    </List.Item>
                                                )}
                                            />
                                        </Col>
                                    );
                                }
                                return (
                                    <Col span={24} key={branch.seoSlug}>
                                        <BranchTitle>
                                            <Link href={`/katalog-firm?branch=${branch.seoSlug}`}>
                                                {branch.title}
                                            </Link>
                                        </BranchTitle>
                                    </Col>
                                );
                            })}
                        </Row>
                    )}
                    {whitePaper.tags.length > 0 && (
                        <Section>
                            <Col span={24}>
                                <Divider />
                            </Col>
                            <Col span={24}>
                                <SectionTitle title="Powiązane tagi" level={3} />
                                <Tags tags={whitePaper.tags} />
                            </Col>
                            <Col span={24}>
                                <Divider />
                            </Col>
                        </Section>
                    )}
                </Sider>
            </Row>
            <Section gutter={[16, 32]} justify="center">
                <Col span={24}>
                    <Divider />
                </Col>
                <Col xs={24} md={20}>
                    <Newsletter />
                </Col>
                <Col span={24}>
                    <Divider />
                </Col>
            </Section>
            <Row>
                <Col span={24}>
                    <Subscription issue={latestIssue} />
                </Col>
                <Col span={24}>
                    <Wideboard id={wideboardBottom[0]} />
                </Col>
            </Row>
            <Modal
                visible={showDynamicForm}
                footer={null}
                maskClosable
                onCancel={onCancelDynamicForm}
                destroyOnClose={true}
            >
                <Row justify="center">
                    <Col span={24}>
                        <DynamicForm form={whitePaper.leadForm} onSubmitSuccess={onSubmitSuccess} />
                    </Col>
                </Row>
            </Modal>
        </BasicLayout>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
    const { apiUrl } = getConfig().publicRuntimeConfig;
    return Promise.all([
        fetch(`${apiUrl}/public/api/white-papers/${params.slug}`),
        fetch(`${apiUrl}/public/api/naszadsik?path=white-papers/${params.slug}`)
    ]).then(async ([res1, res2]) => {
        const data = await res1.json();
        if (data.code === 404) {
            return {
                notFound: true
            };
        }
        const ads = await res2.json();

        return { props: { ...convertProps(ads), ...convertProps(data) } };
    });
};

export default WhitePapersDetailsPage;
